import { ROUTER_PATH } from "@config/router";

const RANKING_TYPES = ["stock", "etf", "crypto"] as const;

type EdgenAiRankingHrefAssetType = (typeof RANKING_TYPES)[number];

export interface BuildEdgenAiRankingHrefParams {
  assetType: EdgenAiRankingHrefAssetType;
  /**
   * 路由主标识中的 `id`。
   * 推荐用于 `crypto`：尽量与详情页路由 `/crypto/[id]` 对齐。
   * 仅当非空时写入 query `id`。
   */
  id?: string;
  /**
   * 路由主标识中的 `symbol`。
   * 推荐用于 `stock` / `etf`：与详情页路由 `/stock/[symbol]`、`/etf/[symbol]` 对齐。
   * `crypto` 也可传入作为兼容兜底，但不应替代 `id` 成为主契约。
   * 仅当非空时写入 query `symbol`。
   */
  symbol?: string;
  initialThemeId?: string;
}

/**
 * 排行页 href：`/edgen-ai/ranking?type=...`
 *
 * 约定：
 * - `stock` / `etf`：优先传 `symbol`
 * - `crypto`：优先传 `id`，`symbol` 仅作为兼容兜底
 *
 * 这里使用 `URLSearchParams` 组装 query，因此调用方不需要手动对 `id` / `symbol`
 * 做 `encodeURIComponent`；query 值会自动编码。只有在拼 path segment 时，才需要手动编码。
 *
 * `type` 非法，或 `id` 与 `symbol` trim 后都为空时返回 `null`。
 */
export function buildEdgenAiRankingHref(
  params: BuildEdgenAiRankingHrefParams,
): string | null {
  const { assetType, id, symbol, initialThemeId } = params;
  const tid = (id ?? "").trim();
  const sym = (symbol ?? "").trim();
  const theme = (initialThemeId ?? "").trim();

  if (!RANKING_TYPES.includes(assetType) || (!tid && !sym)) {
    return null;
  }

  const searchParams = new URLSearchParams();
  searchParams.set("type", assetType);
  if (tid) {
    searchParams.set("id", tid);
  }
  if (sym) {
    searchParams.set("symbol", sym);
  }
  if (theme) {
    searchParams.set("initialThemeId", theme);
  }
  return `${ROUTER_PATH.EDGEN_AI_RANKING}?${searchParams.toString()}`;
}
